import { EDGE_TYPE } from '@ent/entity-type';
import {
  compatMenuItemId,
  defaultMenuSectionId,
  menuRecipeEdgeId
} from '../menus/ids';
import { moveInOrder, nextPosition, sortByPosition } from '../menus/ordering';
import { DEFAULT_MENU_CURRENCY } from '../menus/price';
import type {
  AddMenuItemArgs,
  AddRecipeToMenuArgs,
  CreateMenuItemArgs,
  CreateMenuSectionArgs,
  DeleteMenuSectionArgs,
  MoveMenuItemArgs,
  MoveMenuSectionArgs,
  RemoveMenuItemArgs,
  RenameMenuSectionArgs,
  SetMenuCoverArgs,
  SetMenuStatusArgs,
  UpdateMenuItemArgs
} from './mutator-types';
import { zb } from './query-authz';
import { stableId, stableSequences } from './stable-values';

/**
 * Optimistic client halves of menu-mutators.ts (LQD-6). Same contract as
 * client-mutators.ts: deterministic ids/sequences/timestamps from the args,
 * reads through `tx.run(zb...)`, and never a security boundary. A request the
 * server would reject (deleting the last or a non-empty section, a recipe
 * already on the menu) is a local no-op here; the server's error code then
 * reaches the caller through the `.server` promise.
 *
 * Pass `now: Date.now()` from the call site so optimistic `updatedAt` values
 * are real times.
 */

type Tx = any;
type Row = { id: string; position: number; [key: string]: any };

function mutationNow(args: { now?: number | undefined }): number {
  return args.now ?? 0;
}

async function sectionsOf(tx: Tx, menuId: string): Promise<Row[]> {
  return sortByPosition(
    (await tx.run(zb.menuSections.where('menuId', '=', menuId))) as Row[]
  );
}

async function itemsOfSection(tx: Tx, sectionId: string): Promise<Row[]> {
  return sortByPosition(
    (await tx.run(zb.menuItems.where('sectionId', '=', sectionId))) as Row[]
  );
}

async function touchMenu(tx: Tx, menuId: string, now: number) {
  const menu = await tx.run(zb.menus.where('id', '=', menuId).one());
  if (menu) {
    await tx.mutate.menus.update({ id: menuId, updatedAt: now });
  }
}

async function applyWrites(
  tx: Tx,
  table: 'menuSections' | 'menuItems',
  writes: { id: string; position: number }[],
  now: number
) {
  for (const write of writes) {
    await tx.mutate[table].update({
      id: write.id,
      position: write.position,
      updatedAt: now
    });
  }
}

async function insertMenuRecipeEdge(tx: Tx, menuId: string, recipeId: string) {
  const id = menuRecipeEdgeId(menuId, recipeId);
  const existing = await tx.run(zb.edges.where('id', '=', id).one());
  if (existing) return;
  const [edgeSeq] = stableSequences(`${menuId}:${recipeId}`, 1);
  await tx.mutate.edges.insert({
    id,
    sourceId: menuId,
    targetId: recipeId,
    globalSequence: edgeSeq
  });
}

async function insertItem(
  tx: Tx,
  args: {
    id: string;
    organizationId: string;
    menuId: string;
    sectionId: string;
    recipeId: string;
    priceCents?: number | null | undefined;
    currency?: string | undefined;
    description?: string | null | undefined;
  },
  now: number
): Promise<boolean> {
  const onMenu = await tx.run(
    zb.menuItems
      .where('menuId', '=', args.menuId)
      .where('recipeId', '=', args.recipeId)
      .one()
  );
  if (onMenu) return false;
  const items = await itemsOfSection(tx, args.sectionId);
  await tx.mutate.menuItems.insert({
    id: args.id,
    organizationId: args.organizationId,
    menuId: args.menuId,
    sectionId: args.sectionId,
    recipeId: args.recipeId,
    position: nextPosition(items),
    priceCents: args.priceCents ?? null,
    currency: (args.currency ?? DEFAULT_MENU_CURRENCY).toUpperCase(),
    description: args.description?.trim() || null,
    createdAt: now,
    updatedAt: now,
    globalSequence: stableSequences(args.id, 1)[0]
  });
  await insertMenuRecipeEdge(tx, args.menuId, args.recipeId);
  return true;
}

/** Inserts the default unnamed section for a menu that has none locally. */
export async function insertDefaultSection(
  tx: Tx,
  args: {
    menuId: string;
    organizationId: string;
    sectionId?: string | undefined;
  },
  now: number
) {
  const sections = await sectionsOf(tx, args.menuId);
  if (sections.length) return sections;
  const row = {
    id: args.sectionId ?? defaultMenuSectionId(args.menuId),
    organizationId: args.organizationId,
    menuId: args.menuId,
    name: '',
    position: 0,
    createdAt: now,
    updatedAt: now,
    globalSequence: stableSequences(
      args.sectionId ?? defaultMenuSectionId(args.menuId),
      1
    )[0]
  };
  await tx.mutate.menuSections.insert(row);
  return [row];
}

export const menuSectionClientMutators = {
  create: async (tx: Tx, args: CreateMenuSectionArgs) => {
    const now = mutationNow(args);
    const existing = await tx.run(
      zb.menuSections.where('id', '=', args.id).one()
    );
    if (existing) return;
    const sections = await sectionsOf(tx, args.menuId);
    await tx.mutate.menuSections.insert({
      id: args.id,
      organizationId: args.organizationId,
      menuId: args.menuId,
      name: (args.name ?? '').trim(),
      position: nextPosition(sections),
      createdAt: now,
      updatedAt: now,
      globalSequence: stableSequences(args.id, 1)[0]
    });
    await touchMenu(tx, args.menuId, now);
  },
  rename: async (tx: Tx, args: RenameMenuSectionArgs) => {
    const now = mutationNow(args);
    const section = await tx.run(
      zb.menuSections.where('id', '=', args.id).one()
    );
    if (!section) return;
    await tx.mutate.menuSections.update({
      id: args.id,
      name: args.name.trim(),
      updatedAt: now
    });
    await touchMenu(tx, section.menuId, now);
  },
  move: async (tx: Tx, args: MoveMenuSectionArgs) => {
    const now = mutationNow(args);
    const section = await tx.run(
      zb.menuSections.where('id', '=', args.id).one()
    );
    if (!section) return;
    const writes = moveInOrder(
      await sectionsOf(tx, section.menuId),
      args.id,
      args.direction
    );
    if (!writes.length) return;
    await applyWrites(tx, 'menuSections', writes, now);
    await touchMenu(tx, section.menuId, now);
  },
  delete: async (tx: Tx, args: DeleteMenuSectionArgs) => {
    const now = mutationNow(args);
    const section = await tx.run(
      zb.menuSections.where('id', '=', args.id).one()
    );
    if (!section) return;
    const sections = await sectionsOf(tx, section.menuId);
    const items = await itemsOfSection(tx, args.id);
    // The server throws menus/last-section or menus/section-not-empty.
    if (sections.length <= 1 || items.length) return;
    await tx.mutate.menuSections.delete({ id: args.id });
    await touchMenu(tx, section.menuId, now);
  }
};

export const menuItemClientMutators = {
  add: async (tx: Tx, args: AddMenuItemArgs) => {
    const now = mutationNow(args);
    const existing = await tx.run(zb.menuItems.where('id', '=', args.id).one());
    if (existing) return;
    if (await insertItem(tx, args, now)) {
      await touchMenu(tx, args.menuId, now);
    }
  },
  create: async (tx: Tx, args: CreateMenuItemArgs) => {
    const now = mutationNow(args);
    const existing = await tx.run(zb.menuItems.where('id', '=', args.id).one());
    if (existing) return;
    const recipe = await tx.run(
      zb.recipes.where('id', '=', args.recipeId).one()
    );
    if (!recipe) {
      // Same optimistic shape as recipes.create in client-mutators.ts.
      const [recipeSeq, edgeSeq] = stableSequences(args.recipeId, 2);
      await tx.mutate.recipes.insert({
        id: args.recipeId,
        name: args.name.trim(),
        subtitle: '',
        instructions: '',
        notes: null,
        status: 'draft',
        preparation: null,
        serving: null,
        service: null,
        unitSystem: null,
        createdAt: now,
        updatedAt: now,
        globalSequence: recipeSeq
      });
      await tx.mutate.edges.insert({
        id: stableId(EDGE_TYPE, args.recipeId, 'org'),
        sourceId: args.organizationId,
        targetId: args.recipeId,
        globalSequence: edgeSeq
      });
    }
    if (await insertItem(tx, args, now)) {
      await touchMenu(tx, args.menuId, now);
    }
  },
  update: async (tx: Tx, args: UpdateMenuItemArgs) => {
    const now = mutationNow(args);
    const item = await tx.run(zb.menuItems.where('id', '=', args.id).one());
    if (!item) return;
    if (args.name !== undefined) {
      const recipe = await tx.run(
        zb.recipes.where('id', '=', item.recipeId).one()
      );
      if (recipe && recipe.name !== args.name.trim()) {
        await tx.mutate.recipes.update({
          id: item.recipeId,
          name: args.name.trim(),
          updatedAt: now
        });
      }
    }
    const patch: Record<string, unknown> = {};
    if (args.sectionId !== undefined && args.sectionId !== item.sectionId) {
      const target = await tx.run(
        zb.menuSections.where('id', '=', args.sectionId).one()
      );
      if (!target || target.menuId !== item.menuId) return;
      patch.sectionId = args.sectionId;
      patch.position = nextPosition(await itemsOfSection(tx, args.sectionId));
    }
    if (args.priceCents !== undefined) patch.priceCents = args.priceCents;
    if (args.currency !== undefined) {
      patch.currency = args.currency.toUpperCase();
    }
    if (args.description !== undefined) {
      patch.description = args.description?.trim() || null;
    }
    if (Object.keys(patch).length) {
      await tx.mutate.menuItems.update({
        id: args.id,
        ...patch,
        updatedAt: now
      });
    }
    await touchMenu(tx, item.menuId, now);
  },
  move: async (tx: Tx, args: MoveMenuItemArgs) => {
    const now = mutationNow(args);
    const item = await tx.run(zb.menuItems.where('id', '=', args.id).one());
    if (!item) return;
    const writes = moveInOrder(
      await itemsOfSection(tx, item.sectionId),
      args.id,
      args.direction
    );
    if (!writes.length) return;
    await applyWrites(tx, 'menuItems', writes, now);
    await touchMenu(tx, item.menuId, now);
  },
  remove: async (tx: Tx, args: RemoveMenuItemArgs) => {
    const now = mutationNow(args);
    const item = await tx.run(zb.menuItems.where('id', '=', args.id).one());
    if (!item) return;
    await tx.mutate.menuItems.delete({ id: args.id });
    const edgeId = menuRecipeEdgeId(item.menuId, item.recipeId);
    if (await tx.run(zb.edges.where('id', '=', edgeId).one())) {
      await tx.mutate.edges.delete({ id: edgeId });
    }
    await touchMenu(tx, item.menuId, now);
  }
};

export const menuExtraClientMutators = {
  setStatus: async (tx: Tx, args: SetMenuStatusArgs) => {
    await tx.mutate.menus.update({
      id: args.id,
      status: args.status,
      updatedAt: mutationNow(args)
    });
  },
  setCover: async (tx: Tx, args: SetMenuCoverArgs) => {
    await tx.mutate.menus.update({
      id: args.id,
      coverFileId: args.fileId,
      updatedAt: mutationNow(args)
    });
  },
  addRecipe: async (tx: Tx, args: AddRecipeToMenuArgs) => {
    const now = mutationNow(args);
    await insertMenuRecipeEdge(tx, args.menuId, args.recipeId);
    const menu = await tx.run(zb.menus.where('id', '=', args.menuId).one());
    const sections = await sectionsOf(tx, args.menuId);
    const last = sections[sections.length - 1];
    // Without a local section the organization id is unknown; the server
    // still writes the item and Zero syncs it down.
    if (!menu || !last) return;
    if (
      await insertItem(
        tx,
        {
          id: compatMenuItemId(args.menuId, args.recipeId),
          organizationId: last.organizationId,
          menuId: args.menuId,
          sectionId: last.id,
          recipeId: args.recipeId
        },
        now
      )
    ) {
      await touchMenu(tx, args.menuId, now);
    }
  }
};
